import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

function authHeaders() {
  const t = (localStorage.getItem("argus_checkout") && localStorage.getItem("argus_token")) || localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const { checkAuth } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", message: "Confirming your payment with Stripe…" });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", message: "Missing session_id in the URL." });
      return;
    }
    let attempts = 0;
    const maxAttempts = 8;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`, { headers: authHeaders() });
        if (data.payment_status === "paid") {
          localStorage.removeItem("argus_checkout");
          const isArgus = (data.plan || "").startsWith("argus_");
          const isWhip = data.plan === "argus_whip"; const isLawyer = data.plan === "argus_lawyer";
          setState({ phase: "success", argus: isArgus, whip: isWhip, lawyer: isLawyer, message: isLawyer ? "Your firm's directory listing is live for 30 days." : isWhip ? "Argus WHIP unlocked for 15 days. Start building your fraud report." : isArgus ? `Argus ${data.plan === "argus_corporate" ? "Corporate" : "Journalist"} unlocked for 30 days. Your research desk is ready.` : `${data.plan === "broadcaster" ? "Broadcaster" : "Premium"} unlocked for 30 days. Welcome aboard.` });
          try { await checkAuth?.(); } catch (_) {}
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "error", message: "Your checkout session expired. Please try again." });
          return;
        }
        if (attempts >= maxAttempts) {
          setState({ phase: "pending", message: "Stripe is still processing your payment. We'll update your account automatically when it clears — refresh the dashboard in a minute." });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts >= maxAttempts) {
          setState({ phase: "error", message: "Couldn't reach the payment service. If you were charged, refresh in a minute — the webhook will catch up." });
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, checkAuth]);

  const Icon = state.phase === "success" ? CheckCircle2 : state.phase === "error" ? XCircle : Loader2;
  const tone =
    state.phase === "success" ? "text-emerald-400" :
    state.phase === "error" ? "text-red-400" :
    "text-emerald-500 animate-spin";

  return (
    <main className="max-w-md mx-auto px-5 py-32 text-center" data-testid="billing-success-page">
      <Icon className={`w-14 h-14 mx-auto ${tone}`} />
      <h1 className="fl-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-6">
        {state.phase === "success" ? "Payment successful" : state.phase === "error" ? "Payment issue" : "Processing…"}
      </h1>
      <p className="text-slate-300 mt-4 text-sm leading-relaxed">{state.message}</p>
      <div className="mt-8 flex gap-2 justify-center flex-wrap">
        <Link to={state.lawyer ? "/argus/lawyers" : state.whip ? "/argus/whip" : state.argus ? "/argus" : "/dashboard"} className="fl-btn" data-testid="billing-go-dashboard">
          {state.lawyer ? "View directory" : state.whip ? "Open WHIP" : state.argus ? "Open Argus" : "Open Studio"} <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="/pricing" className="fl-btn ghost" data-testid="billing-back-pricing">
          Back to plans
        </Link>
      </div>
      {sessionId && <p className="mt-8 fl-mono text-[10px] tracking-widest uppercase text-slate-600">// Session: {sessionId.slice(0, 16)}…</p>}
    </main>
  );
}
